<template>
  <div class="article-page">
    <HeadNav
      selected-language="en"
      page="how-to"
      :gotodownload="gotodownload"
    />

    <main class="article-main">
      <nav class="breadcrumb" aria-label="Breadcrumb">
        <a href="/">Home</a>
        <span aria-hidden="true">/</span>
        <a href="/how-to/">How-to</a>
        <span aria-hidden="true">/</span>
        <span aria-current="page">{{ article.h1 }}</span>
      </nav>

      <article class="guide-article">
        <header class="article-header">
          <h1>{{ article.h1 }}</h1>
        </header>

        <div class="article-body">
          <template v-for="(block, index) in article.blocks">
            <h2
              v-if="block.type === 'h2'"
              :key="`h2-${index}`"
            >
              {{ block.text }}
            </h2>
            <h3
              v-else-if="block.type === 'h3'"
              :key="`h3-${index}`"
            >
              {{ block.text }}
            </h3>
            <p
              v-else-if="block.type === 'p'"
              :key="`p-${index}`"
              v-html="block.html"
            ></p>
            <ul
              v-else-if="block.type === 'ul'"
              :key="`ul-${index}`"
              class="guide-list"
            >
              <li
                v-for="(item, itemIndex) in block.items"
                :key="`ul-${index}-${itemIndex}`"
                v-html="item"
              ></li>
            </ul>
            <ol
              v-else-if="block.type === 'ol'"
              :key="`ol-${index}`"
              class="guide-list guide-list--ordered"
            >
              <li
                v-for="(item, itemIndex) in block.items"
                :key="`ol-${index}-${itemIndex}`"
                v-html="item"
              ></li>
            </ol>
            <ul
              v-else-if="block.type === 'checklist'"
              :key="`checklist-${index}`"
              class="guide-list guide-list--checklist"
            >
              <li
                v-for="(item, itemIndex) in block.items"
                :key="`checklist-${index}-${itemIndex}`"
                v-html="item"
              ></li>
            </ul>
          </template>
        </div>
      </article>

      <nav class="article-pagination" aria-label="Article navigation">
        <a :href="previousArticle.path" class="pagination-link pagination-link--previous">
          <span class="pagination-label">
            <i class="fa fa-arrow-left" aria-hidden="true"></i>
            Previous guide
          </span>
          <strong>{{ previousArticle.h1 }}</strong>
        </a>
        <a :href="nextArticle.path" class="pagination-link pagination-link--next">
          <span class="pagination-label">
            Next guide
            <i class="fa fa-arrow-right" aria-hidden="true"></i>
          </span>
          <strong>{{ nextArticle.h1 }}</strong>
        </a>
      </nav>
    </main>
  </div>
</template>

<script>
import '@/css/base.scss';
import '@/css/index.scss';
import HeadNav from '@/components/HeadNav.vue';
import howToArticles from '@/constants/howToArticles';

export default {
  name: 'HowToArticle',
  components: {
    HeadNav,
  },
  computed: {
    article() {
      const slug = this.$route.meta.articleSlug;
      return howToArticles.find((item) => item.slug === slug) || howToArticles[0];
    },
    articleIndex() {
      return howToArticles.findIndex((item) => item.slug === this.article.slug);
    },
    previousArticle() {
      const index = (this.articleIndex - 1 + howToArticles.length) % howToArticles.length;
      return howToArticles[index];
    },
    nextArticle() {
      const index = (this.articleIndex + 1) % howToArticles.length;
      return howToArticles[index];
    },
  },
  methods: {
    gotodownload() {
      if (typeof window !== 'undefined') {
        window.location.href = '/download-apk/';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.article-page {
  min-height: 100vh;
  background: #f5f8f7;
  color: #14213d;
}

.article-main {
  width: min(920px, calc(100% - 32px));
  margin: 0 auto;
  padding: 24px 0 64px;
}

.breadcrumb {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 4px 0 18px;
  color: #64748b;
  font-size: 13px;

  a {
    color: #0b6d47;
    font-weight: 600;
    text-decoration: none;
  }

  span[aria-current="page"] {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.guide-article {
  overflow: hidden;
  border: 1px solid #dfe9e4;
  border-radius: 9px;
  background: #fff;
  box-shadow: 0 10px 28px rgba(23, 70, 52, 0.06);
}

.article-header {
  padding: 42px 46px 34px;
  border-bottom: 1px solid #e7efeb;
  background: linear-gradient(140deg, #f6fcf9 0%, #fff 72%);
}

.article-header h1 {
  max-width: 760px;
  margin: 0;
  color: #102b21;
  font-size: clamp(32px, 5vw, 48px);
  line-height: 1.15;
  letter-spacing: -0.02em;
}

.article-body {
  padding: 14px 46px 42px;
  color: #41554d;
  font-size: 16px;
  line-height: 1.8;

  > *:first-child {
    margin-top: 0;
  }

  > h2 {
    margin: 30px 0 14px;
    color: #173b2e;
    font-size: 24px;
    line-height: 1.35;
  }

  > h3 {
    margin: 24px 0 10px;
    color: #173b2e;
    font-size: 19px;
    line-height: 1.4;
  }

  > p {
    margin: 0 0 16px;
  }

  ::v-deep a {
    color: #1e73be;
    font-weight: 700;
  }

  ::v-deep a:hover,
  ::v-deep a:focus,
  ::v-deep a:active {
    color: #2880ba;
    background-color: transparent;
  }

  ::v-deep strong {
    color: #173b2e;
    font-weight: 800;
  }

  ::v-deep a strong {
    color: inherit;
  }
}

.guide-list {
  margin: 2px 0 18px;
  padding: 0;
  list-style: none !important;
}

.guide-list li {
  position: relative;
  margin-bottom: 10px;
  padding-left: 24px;
  color: #41554d;
  font-size: 16px;
  line-height: 1.7;
}

.guide-list li::before {
  position: absolute;
  top: 0.78em;
  left: 2px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #28a873;
  content: "";
  transform: translateY(-50%);
}

.guide-list li ::v-deep a {
  color: #1e73be;
  font-weight: 700;
}

.guide-list li ::v-deep a:hover,
.guide-list li ::v-deep a:focus,
.guide-list li ::v-deep a:active {
  color: #2880ba;
  background-color: transparent;
}

.guide-list li ::v-deep strong {
  color: #173b2e;
  font-weight: 800;
}

.guide-list li ::v-deep a strong {
  color: inherit;
}

.guide-list--ordered {
  padding-left: 24px;
  list-style: decimal !important;
}

.guide-list--ordered li {
  padding-left: 4px;
}

.guide-list--ordered li::before {
  display: none;
}

.guide-list--checklist li {
  padding-left: 28px;
}

.guide-list--checklist li::before {
  top: 0.72em;
  left: 0;
  width: 14px;
  height: 14px;
  border: 2px solid #28a873;
  border-radius: 3px;
  background: transparent;
}

.article-pagination {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 20px;
}

.pagination-link {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 8px;
  padding: 20px;
  border: 1px solid #dfe9e4;
  border-radius: 8px;
  background: #fff;
  color: #14213d;
  text-decoration: none;
  box-shadow: 0 5px 18px rgba(28, 67, 53, 0.04);

  strong {
    font-size: 15px;
    line-height: 1.45;
  }

  &:hover,
  &:focus {
    border-color: #b8dcc9;
    color: #0b6d47;
  }
}

.pagination-link--next {
  text-align: right;
}

.pagination-label {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #0b6d47;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.pagination-link--next .pagination-label {
  justify-content: flex-end;
}

@media (max-width: 640px) {
  .article-main {
    width: min(100% - 20px, 920px);
    padding-top: 16px;
  }

  .article-header {
    padding: 30px 20px 24px;
  }

  .article-header h1 {
    font-size: 34px;
  }

  .article-body {
    padding: 10px 20px 30px;

    > h2 {
      font-size: 22px;
    }

    > h3 {
      font-size: 18px;
    }
  }

  .article-pagination {
    grid-template-columns: 1fr;
  }

  .pagination-link--next {
    text-align: left;
  }

  .pagination-link--next .pagination-label {
    justify-content: flex-start;
  }
}
</style>
